import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../services/api';
import {
  DEALERSHIP_PHONE_DISPLAY,
} from '../../constants/dealershipContact';
import DealershipPhoneLink from '../../components/ALLJsx/DealershipPhoneLink';
import { 
  Calendar, 
  Car,
  Clock,
  MapPin,
  CheckCircle,
  AlertCircle,
  Shield,
  CreditCard,
  DollarSign,
  TrendingUp,
  FileText,
  ChevronRight,
  Package,
  Upload,
  Download,
  Trash2,
  File,
  Star,
} from 'lucide-react';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import RecentlyViewedCars from '../../components/ALLJsx/RecentlyViewedCars';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import '../AllCss/Dashboard.css';

function Dashboard() {
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const toast = useToast();
  const confirmDialog = useConfirm();

  // State for appointments
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(true);
  
  // State for reschedule modal
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // State for documents
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleNotes, setRescheduleNotes] = useState('');
  const [rescheduleError, setRescheduleError] = useState(null);
  const [rescheduleSuccess, setRescheduleSuccess] = useState(null);
  const [pendingServiceSurveys, setPendingServiceSurveys] = useState([]);

  // Generate time slots from 9:00 AM to 5:00 PM in 15-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    // 9 AM to 11:45 AM
    for (let hour = 9; hour < 12; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = `${hour}:${minute.toString().padStart(2, '0')}`;
        const display = `${hour}:${minute.toString().padStart(2, '0')} AM`;
        slots.push({ value: time, display });
      }
    }
    // 12:00 PM to 12:45 PM
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `12:${minute.toString().padStart(2, '0')}`;
      const display = `12:${minute.toString().padStart(2, '0')} PM`;
      slots.push({ value: time, display });
    }
    // 1 PM to 5:00 PM
    for (let hour = 1; hour <= 5; hour++) {
      const endMinute = hour === 5 ? 1 : 60; // Only 5:00 PM, not 5:15, 5:30, 5:45
      for (let minute = 0; minute < endMinute; minute += 15) {
        const time24 = `${hour + 12}:${minute.toString().padStart(2, '0')}`;
        const display = `${hour}:${minute.toString().padStart(2, '0')} PM`;
        slots.push({ value: time24, display });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const fetchAppointments = async () => {
    if (!user) return;
    
    try {
      setLoadingAppointments(true);
      const response = await axios.get(`${API_URL}/api/appointments`, {
        withCredentials: true,
              });
      
      const now = new Date();
      const appointmentsToMarkCompleted = [];
      
      // Filter for upcoming appointments only:
      // 1. Not completed, cancelled, or no_show
      // 2. Appointment date/time is in the future
      const upcoming = response.data.data.appointments.filter(apt => {
        // Skip if already completed, cancelled, or no_show
        if (apt.status === 'completed' || apt.status === 'cancelled' || apt.status === 'no_show') {
          return false;
        }
        
        // Check if appointment time has passed
        const appointmentDate = new Date(apt.appointmentDate);
        if (appointmentDate <= now) {
          // Appointment has passed, mark it for completion
          appointmentsToMarkCompleted.push(apt.id);
          return false;
        }
        
        return true;
      });
      
      // Mark past appointments as completed in the background (don't wait for it)
      if (appointmentsToMarkCompleted.length > 0) {
        appointmentsToMarkCompleted.forEach(async (appointmentId) => {
          try {
            await axios.put(
              `${API_URL}/api/appointments/${appointmentId}`,
              { status: 'completed' },
              {
        withCredentials: true,
                              }
            );
          } catch (error) {
            console.error(`Error marking appointment ${appointmentId} as completed:`, error);
            // Don't block the UI if this fails
          }
        });
      }
      
      setAppointments(upcoming);

      try {
        const pr = await axios.get(`${API_URL}/api/surveys/service-pending`, {
        withCredentials: true,
        });
        if (pr.data?.success) {
          setPendingServiceSurveys(pr.data.data.pending || []);
        } else {
          setPendingServiceSurveys([]);
        }
      } catch {
        setPendingServiceSurveys([]);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
      setPendingServiceSurveys([]);
    } finally {
      setLoadingAppointments(false);
    }
  };

  // Fetch user appointments
  useEffect(() => {
    fetchAppointments();
    const interval = setInterval(() => {
      fetchAppointments();
    }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh on user only; stable fetch
  }, [user]);

  // Fetch user documents
  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh on user only; stable fetch
  }, [user]);

  // Handle opening reschedule modal
  const handleOpenReschedule = (appointment) => {
    setSelectedAppointment(appointment);
    
    // Set current date/time with validation
    try {
      const currentDate = new Date(appointment.appointmentDate);
      
      // Check if date is valid
      if (!isNaN(currentDate.getTime())) {
        // Set date (YYYY-MM-DD format)
        const dateStr = currentDate.toISOString().split('T')[0];
        setRescheduleDate(dateStr);
        
        // Set time (HH:MM format)
        const hours = currentDate.getHours();
        const minutes = currentDate.getMinutes();
        const timeStr = `${hours}:${minutes.toString().padStart(2, '0')}`;
        setRescheduleTime(timeStr);
      } else {
        // If invalid, use tomorrow at 9:00 AM as default
        const fallbackDate = new Date();
        fallbackDate.setDate(fallbackDate.getDate() + 1);
        const dateStr = fallbackDate.toISOString().split('T')[0];
        setRescheduleDate(dateStr);
        setRescheduleTime('9:00'); // Default to 9:00 AM
      }
    } catch (error) {
      // Fallback to tomorrow at 9:00 AM
      const fallbackDate = new Date();
      fallbackDate.setDate(fallbackDate.getDate() + 1);
      const dateStr = fallbackDate.toISOString().split('T')[0];
      setRescheduleDate(dateStr);
      setRescheduleTime('9:00'); // Default to 9:00 AM
    }
    
    setRescheduleNotes(appointment.notes || '');
    setRescheduleError(null);
    setRescheduleSuccess(null);
    setShowRescheduleModal(true);
  };

  // Handle reschedule submission
  const handleRescheduleSubmit = async (e) => {
    e.preventDefault();
    setRescheduleError(null);
    setRescheduleSuccess(null);

    if (!rescheduleDate || !rescheduleTime) {
      setRescheduleError('Please select both date and time');
      return;
    }

    // Combine date and time
    const [hours, minutes] = rescheduleTime.split(':');
    const selectedDateTime = new Date(rescheduleDate);
    selectedDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    // Check if date/time is in the past
    if (selectedDateTime < new Date()) {
      setRescheduleError('Appointment date and time cannot be in the past');
      return;
    }

    // Validate business hours (9 AM to 5 PM)
    const hour = parseInt(hours);
    const minute = parseInt(minutes);
    if (hour < 9 || hour > 17 || (hour === 17 && minute > 0)) {
      setRescheduleError('Please select a time between 9:00 AM and 5:00 PM');
      return;
    }

    try {
      const appointmentDateISO = selectedDateTime.toISOString();
      
      await axios.put(
        `${API_URL}/api/appointments/${selectedAppointment.id}`,
        {
          appointmentDate: appointmentDateISO,
          notes: rescheduleNotes,
          // If appointment was confirmed, set back to upcoming for new date
          status: selectedAppointment.status === 'confirmed' ? 'upcoming' : selectedAppointment.status,
        },
        {
        withCredentials: true,
                  }
      );

      setRescheduleSuccess('Appointment rescheduled successfully! You will receive a new SMS reminder for the updated date.');
      
      // Refresh appointments after a short delay
      setTimeout(() => {
        fetchAppointments();
        setShowRescheduleModal(false);
        setSelectedAppointment(null);
      }, 1500);
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      setRescheduleError(error.response?.data?.message || 'Failed to reschedule appointment');
    }
  };

  // Handle cancel appointment
  const handleCancelAppointment = async (appointmentId) => {
    const ok = await confirmDialog({
      title: 'Cancel appointment',
      description: 'Are you sure you want to cancel this appointment?',
      confirmLabel: 'Cancel appointment',
      destructive: true,
    });
    if (!ok) return;

    try {
      await axios.put(
        `${API_URL}/api/appointments/${appointmentId}`,
        { status: 'cancelled' },
        {
        withCredentials: true,
                  }
      );
      
      // Refresh appointments
      fetchAppointments();
      toast.success('Appointment cancelled successfully. You will receive a confirmation SMS shortly.');
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Failed to cancel appointment. Please try again.');
    }
  };

  // Fetch documents from API
  const fetchDocuments = async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/documents`, {
        withCredentials: true,
              });
      
      if (response.data.success) {
        const formattedDocs = response.data.data.documents.map(doc => ({
          id: doc.id,
          name: doc.originalFileName,
          size: formatFileSize(doc.fileSize),
          type: getFileType(doc.originalFileName),
          uploadDate: new Date(doc.uploadDate).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
          }),
          url: `${API_URL}${doc.url}`,
          documentId: doc.id,
        }));
        setDocuments(formattedDocs);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: `${API_URL}/api/documents`,
        code: error.code,
      });
      
      // Handle different error types
      if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
        setUploadError('Network error: Cannot connect to server. Please check your connection.');
      } else if (error.response?.status === 401) {
        setUploadError('Authentication failed. Please log in again.');
      } else if (error.response?.status === 404) {
        // Route not found - might be server not restarted
        setUploadError('Documents endpoint not found. Server may need to be restarted.');
      } else if (error.response?.status === 500) {
        setUploadError('Server error. Please try again later.');
      } else if (error.response?.status) {
        setUploadError(`Error ${error.response.status}: ${error.response?.data?.message || 'Failed to load documents.'}`);
      } else {
        setUploadError('Failed to load documents. Please refresh the page.');
      }
      setTimeout(() => setUploadError(null), 7000);
    }
  };

  // Handle document upload
  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      // Upload files one by one
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(
          `${API_URL}/api/documents/upload`,
          formData,
          {
        withCredentials: true,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        
        return response.data.data.document;
      });

      await Promise.all(uploadPromises);

      // Refresh documents list
      await fetchDocuments();
      
      setUploadSuccess(`${files.length} document(s) uploaded successfully!`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setUploadSuccess(null), 3000);
      
      // Reset file input
      e.target.value = '';
    } catch (error) {
      console.error('Error uploading document:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: `${API_URL}/api/documents/upload`,
        code: error.code,
      });
      
      // Handle different error types
      let errorMessage = 'Failed to upload document. Please try again.';
      
      if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
        errorMessage = 'Network error: Cannot connect to server. Please check your connection.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Upload endpoint not found. Server may need to be restarted.';
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.message || 'Invalid file. Please check file type and size (max 10MB).';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setUploadError(errorMessage);
      setTimeout(() => setUploadError(null), 7000);
    } finally {
      setUploading(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Get file type from extension
  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const types = {
      pdf: 'PDF',
      doc: 'Word',
      docx: 'Word',
      jpg: 'Image',
      jpeg: 'Image',
      png: 'Image'
    };
    return types[ext] || ext.toUpperCase();
  };

  // Handle document download
  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(doc.url, {
        withCredentials: true,
                responseType: 'blob',
      });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      setUploadError('Failed to download document. Please try again.');
      setTimeout(() => setUploadError(null), 5000);
    }
  };

  // Handle document delete
  const handleDeleteDocument = async (doc) => {
    if (!doc) {
      console.error('No document provided to delete');
      setUploadError('Error: No document selected.');
      setTimeout(() => setUploadError(null), 5000);
      return;
    }

    const documentId = doc.documentId || doc.id;
    if (!documentId) {
      console.error('No document ID found:', doc);
      setUploadError('Error: Document ID not found.');
      setTimeout(() => setUploadError(null), 5000);
      return;
    }

    const ok = await confirmDialog({
      title: 'Delete document',
      description: `Are you sure you want to delete "${doc.name}"?`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    // Set deleting state
    setDeletingDocumentId(documentId);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      if (!isAuthenticated) {
        setUploadError('Authentication required. Please log in again.');
        setTimeout(() => setUploadError(null), 5000);
        setDeletingDocumentId(null);
        return;
      }

      
      const response = await axios.delete(`${API_URL}/api/documents/${documentId}`, {
        withCredentials: true,
              });
      
      
      // Optimistically remove from UI immediately for better UX
      setDocuments(prev => prev.filter(d => {
        const docId = d.documentId || d.id;
        return docId !== documentId;
      }));
      
      setUploadSuccess('Document deleted successfully!');
      setTimeout(() => setUploadSuccess(null), 3000);
      
      // Refresh documents list to ensure sync (quietly in background)
      try {
        await fetchDocuments();
      } catch (refreshError) {
        console.warn('Could not refresh documents list:', refreshError);
        // Don't show error to user since delete was successful
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: `${API_URL}/api/documents/${doc.documentId || doc.id}`,
        code: error.code,
      });
      
      let errorMessage = 'Failed to delete document. Please try again.';
      
      if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
        errorMessage = 'Network error: Cannot connect to server.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Document not found. It may have already been deleted.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setUploadError(errorMessage);
      setTimeout(() => setUploadError(null), 7000);
    } finally {
      setDeletingDocumentId(null);
    }
  };

  if (!user) {
    return (
      <div className="Dashboard">
        <div className="dashboard-container">
          <div className="empty-dashboard">
            <Shield size={64} />
            <h2>Access Restricted</h2>
            <p>Please log in to view your dashboard</p>
            <Link to="/auth" className="btn-primary">
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Format appointments for display
  const upcomingAppointments = appointments.map(apt => ({
    ...apt, // Keep all original data
    id: apt.id,
    type: apt.type.replace('_', ' ').toUpperCase(),
    carDisplay: apt.car ? `${apt.car.year} ${apt.car.make} ${apt.car.model}` : 'General Consultation',
    date: new Date(apt.appointmentDate).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }),
    time: new Date(apt.appointmentDate).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    }),
    location: 'Ocean Springs, MS', // Can be made dynamic if you add location field
    status: apt.status,
    rawDate: apt.appointmentDate
  })).sort((a, b) => new Date(a.rawDate) - new Date(b.rawDate)); // Sort by date

  return (
    <div className="Dashboard">
      <Navbar />
      <div className="dashboard-container">
        {/* Welcome Header */}
        <div className="dashboard-welcome">
          <h1>Welcome back, {user.fullName || user.email}!</h1>
          <p>Manage your car buying journey all in one place</p>
        </div>

        {/* Recently Viewed Cars */}
        <RecentlyViewedCars />

        <section className="dashboard-section" id="customer-surveys">
          <div className="section-header">
            <div className="section-title-group">
              <Star className="section-icon" size={24} />
              <h2>Your feedback</h2>
            </div>
          </div>
          <div className="section-content">
            <p className="survey-section-lead">
              Quick NPS, share feature and UI ideas for our roadmap, or rate a completed visit.
            </p>
            <div className="survey-prompt-row">
              <Link to="/survey/nps" className="btn-primary-sm">Quick NPS (30 seconds)</Link>
              <Link to="/survey/ideas" className="btn-secondary-sm">Suggest features &amp; improvements</Link>
            </div>
            {pendingServiceSurveys.length > 0 && (
              <div className="survey-pending-block">
                <h3 className="survey-pending-title">Rate a recent visit</h3>
                <ul className="survey-pending-list">
                  {pendingServiceSurveys.map((p) => (
                    <li key={p.appointmentId}>
                      <span className="survey-pending-meta">
                        {p.car ? `${p.car.year} ${p.car.make} ${p.car.model}` : 'Appointment'}
                        {' · '}
                        {p.appointmentDate ? new Date(p.appointmentDate).toLocaleDateString() : ''}
                      </span>
                      <Link
                        to={`/survey/service?appointmentId=${encodeURIComponent(p.appointmentId)}`}
                        className="btn-primary-sm survey-pending-link"
                      >
                        Rate this visit
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>

        {/* Main Content Grid */}
        <div className="dashboard-grid">
          {/* Appointments Section */}
          <div className="dashboard-section">
            <div className="section-header">
              <div className="section-title-group">
                <Car className="section-icon" size={24} />
                <h2>My Appointments</h2>
              </div>
              <Link to="/appointments" className="view-all-link">
                View All <ChevronRight size={16} />
              </Link>
            </div>
            
            <div className="section-content">
              {loadingAppointments ? (
                <div className="empty-section">
                  <Calendar size={48} />
                  <p>Loading appointments...</p>
                </div>
              ) : upcomingAppointments.length > 0 ? (
                upcomingAppointments.slice(0, 3).map((apt) => (
                  <div key={apt.id} className="appointment-item">
                    <div className="appointment-left">
                      <div className="appointment-type-badge">{apt.type}</div>
                      <div className={`appointment-status-badge status-${apt.status}`}>
                        {apt.status === 'confirmed' ? (
                          <><CheckCircle size={14} /> Confirmed</>
                        ) : apt.status === 'pending' ? (
                          <><AlertCircle size={14} /> Pending</>
                        ) : (
                          <><CheckCircle size={14} /> {apt.status.charAt(0).toUpperCase() + apt.status.slice(1)}</>
                        )}
                      </div>
                    </div>
                    
                    <h3 className="appointment-car">{apt.carDisplay}</h3>
                    
                    <div className="appointment-info">
                      <div className="info-item">
                        <Calendar size={16} />
                        <span>{apt.date}</span>
                      </div>
                      <div className="info-item">
                        <Clock size={16} />
                        <span>{apt.time}</span>
                      </div>
                      <div className="info-item">
                        <MapPin size={16} />
                        <span>{apt.location}</span>
                      </div>
                    </div>
                    
                    <div className="appointment-actions">
                      {(apt.status === 'upcoming' || apt.status === 'confirmed') && (
                        <>
                      <button 
                        className="btn-secondary-sm"
                        onClick={() => handleOpenReschedule(apt)}
                      >
                        Reschedule
                      </button>
                      <button 
                        className="btn-text-sm"
                        onClick={() => handleCancelAppointment(apt.id)}
                      >
                        Cancel
                      </button>
                        </>
                      )}
                      {apt.status === 'in_progress' && (
                        <span className="appointment-note">Appointment in progress</span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-section">
                  <Calendar size={48} />
                  <p>No upcoming appointments</p>
                  <Link to="/inventory" className="btn-primary-sm">
                    Browse Cars
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Financing Section */}
          <div className="dashboard-section">
            <div className="section-header">
              <div className="section-title-group">
                <CreditCard className="section-icon" size={24} />
                <h2>Financing</h2>
              </div>
              <span className="coming-soon-badge">Coming Soon</span>
            </div>
            
            <div className="section-content">
              <div className="feature-placeholder">
                <div className="placeholder-icon">
                  <TrendingUp size={48} />
                </div>
                <h3>Get Pre-Qualified</h3>
                <p>Check your personalized rates with no impact to your credit score</p>
                <ul className="feature-list">
                  <li>
                    <CheckCircle size={16} />
                    <span>See personalized rates in minutes</span>
                  </li>
                  <li>
                    <CheckCircle size={16} />
                    <span>No impact to your credit</span>
                  </li>
                  <li>
                    <CheckCircle size={16} />
                    <span>Rates as low as 3.99% APR</span>
                  </li>
                  <li>
                    <CheckCircle size={16} />
                    <span>Flexible terms up to 84 months</span>
                  </li>
                </ul>
                <button className="btn-primary-disabled" disabled>
                  Get Pre-Qualified (Coming Soon)
                </button>
              </div>
            </div>
          </div>

          {/* Sell/Trade-In Section */}
          <div className="dashboard-section">
            <div className="section-header">
              <div className="section-title-group">
                <Package className="section-icon" size={24} />
                <h2>Sell or Trade-In</h2>
              </div>
              <span className="coming-soon-badge">Coming Soon</span>
            </div>
            
            <div className="section-content">
              <div className="feature-placeholder">
                <div className="placeholder-icon">
                  <DollarSign size={48} />
                </div>
                <h3>Get Your Car's Value</h3>
                <p>Get a real cash offer for your car in minutes</p>
                <ul className="feature-list">
                  <li>
                    <CheckCircle size={16} />
                    <span>Get instant online offer</span>
                  </li>
                  <li>
                    <CheckCircle size={16} />
                    <span>Valid for 7 days</span>
                  </li>
                  <li>
                    <CheckCircle size={16} />
                    <span>Trade-in or sell directly</span>
                  </li>
                  <li>
                    <CheckCircle size={16} />
                    <span>We handle all paperwork</span>
                  </li>
                </ul>
                <button className="btn-primary-disabled" disabled>
                  Get My Offer (Coming Soon)
                </button>
              </div>
            </div>
          </div>

          {/* Documents Section */}
          <div className="dashboard-section">
            <div className="section-header">
              <div className="section-title-group">
                <FileText className="section-icon" size={24} />
                <h2>My Documents</h2>
              </div>
              <label htmlFor="document-upload" className="upload-btn-label">
                <Upload size={18} />
                Upload Document
                <input
                  type="file"
                  id="document-upload"
                  className="document-upload-input"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  multiple
                  onChange={handleDocumentUpload}
                  disabled={uploading}
                />
              </label>
            </div>
            
            <div className="section-content">
              {uploadError && (
                <div className="alert alert-error">
                  <AlertCircle size={16} />
                  {uploadError}
                </div>
              )}
              
              {uploadSuccess && (
                <div className="alert alert-success">
                  <CheckCircle size={16} />
                  {uploadSuccess}
                </div>
              )}

              {documents.length === 0 ? (
                <div className="empty-section">
                  <FileText size={48} />
                  <p>No documents available</p>
                  <p className="empty-description">
                    Your purchase agreements, financing documents, and trade-in paperwork will appear here
                  </p>
                  <label htmlFor="document-upload-empty" className="btn-primary-sm">
                    <Upload size={18} />
                    Upload Your First Document
                    <input
                      type="file"
                      id="document-upload-empty"
                      className="document-upload-input"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      multiple
                      onChange={handleDocumentUpload}
                      disabled={uploading}
                    />
                  </label>
                </div>
              ) : (
                <div className="documents-list">
                  {documents.map((doc, index) => (
                    <div key={doc.id || index} className="document-item">
                      <div className="document-info">
                        <div className="document-icon">
                          <File size={24} />
                        </div>
                        <div className="document-details">
                          <h4 className="document-name">{doc.name}</h4>
                          <p className="document-meta">
                            {doc.size} • {doc.type} • {doc.uploadDate}
                          </p>
                        </div>
                      </div>
                      <div className="document-actions">
                        <button 
                          className="action-btn download-btn"
                          onClick={() => handleDownloadDocument(doc)}
                          title="Download"
                        >
                          <Download size={18} />
                        </button>
                        <button 
                          className="action-btn delete-btn"
                          onClick={() => handleDeleteDocument(doc)}
                          disabled={deletingDocumentId === (doc.documentId || doc.id)}
                          title={deletingDocumentId === (doc.documentId || doc.id) ? "Deleting..." : "Delete"}
                        >
                          {deletingDocumentId === (doc.documentId || doc.id) ? (
                            <span style={{fontSize: '12px'}}>...</span>
                          ) : (
                            <Trash2 size={18} />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {uploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div className="progress-fill"></div>
                  </div>
                  <p>Uploading document...</p>
                </div>
              )}
            </div>
          </div>

          {/* Help & Support */}
          <div className="dashboard-section help-section">
            <div className="help-content">
              <h3>Need Help?</h3>
              <p>Our team is here to assist you with any questions</p>
              <div className="help-actions">
                <Link to="/contact" className="btn-secondary">
                  Contact Us
                </Link>
                <DealershipPhoneLink className="btn-text">
                  Call: {DEALERSHIP_PHONE_DISPLAY}
                </DealershipPhoneLink>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reschedule Appointment Modal */}
      {showRescheduleModal && selectedAppointment && (
        <div className="modal-overlay" onClick={() => setShowRescheduleModal(false)}>
          <div className="modal-content reschedule-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reschedule Appointment</h3>
              <button 
                className="modal-close"
                onClick={() => setShowRescheduleModal(false)}
              >
                ×
              </button>
            </div>

            {/* Appointment Preview */}
            <div className="appointment-preview">
              <div className="preview-car">
                {selectedAppointment.car?.images && selectedAppointment.car.images[0] && (
                  <img 
                    src={selectedAppointment.car.images[0]} 
                    alt={`${selectedAppointment.car.year} ${selectedAppointment.car.make} ${selectedAppointment.car.model}`}
                  />
                )}
                <div className="preview-details">
                  <h4>
                    {selectedAppointment.car 
                      ? `${selectedAppointment.car.year} ${selectedAppointment.car.make} ${selectedAppointment.car.model}`
                      : 'General Inquiry'}
                  </h4>
                  <span className="appointment-type-badge">
                    {selectedAppointment.type.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            <form onSubmit={handleRescheduleSubmit}>
              <div className="date-time-container">
                <div className="date-time-row">
                  <div className="form-group date-field">
                    <label htmlFor="reschedule-date">
                      <Calendar size={18} />
                      Select Date
                    </label>
                    <input
                      type="date"
                      id="reschedule-date"
                      value={rescheduleDate}
                      onChange={(e) => setRescheduleDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>

                  <div className="form-group time-field">
                    <label htmlFor="reschedule-time">
                      <Clock size={18} />
                      Select Time (9:00 AM - 5:00 PM)
                    </label>
                    <select
                      id="reschedule-time"
                      value={rescheduleTime}
                      onChange={(e) => setRescheduleTime(e.target.value)}
                      required
                    >
                      <option value="">-- Select Time --</option>
                      {timeSlots.map((slot) => (
                        <option key={slot.value} value={slot.value}>
                          {slot.display}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="time-note-bottom">Available in 15-minute intervals</p>
              </div>

              <div className="form-group">
                <label htmlFor="reschedule-notes">
                  Additional Notes (Optional)
                </label>
                <textarea
                  id="reschedule-notes"
                  value={rescheduleNotes}
                  onChange={(e) => setRescheduleNotes(e.target.value)}
                  rows="3"
                  placeholder="Any special requests or information..."
                />
              </div>

              {rescheduleError && (
                <div className="alert alert-error">
                  <AlertCircle size={18} />
                  {rescheduleError}
                </div>
              )}

              {rescheduleSuccess && (
                <div className="alert alert-success">
                  <CheckCircle size={18} />
                  {rescheduleSuccess}
                </div>
              )}

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-modal btn-cancel"
                  onClick={() => setShowRescheduleModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-modal btn-submit"
                  disabled={!rescheduleDate || !rescheduleTime}
                >
                  Confirm Reschedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}

export default Dashboard;
